import { CreatePayoutBracketDto } from '../dto/tournaments/payout-structure.dto';

export class PayoutBracketValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PayoutBracketValidationError';
  }
}

export type PayoutBracketErrorFactory = (message: string) => Error;

/**
 * Shared validator for payout brackets. Throws using the provided errorFactory
 * (default is PayoutBracketValidationError) so callers (API or client) can map
 * the message to their own error types.
 */
export const validatePayoutBrackets = (
  brackets: CreatePayoutBracketDto[],
  errorFactory: PayoutBracketErrorFactory = (message) => new PayoutBracketValidationError(message),
): void => {
  const throwErr = (message: string): never => {
    throw errorFactory(message);
  };

  if (!brackets.length) {
    throwErr('No brackets specified.');
  }

  // Basic per-bracket sanity checks
  for (const bracket of brackets) {
    if (!bracket || typeof bracket !== 'object') {
      throwErr('Invalid bracket object');
    }
    if (bracket.rankFrom < 1 || bracket.rankTo < 1) {
      throwErr('rankFrom and rankTo must be positive');
    }
    if (bracket.playersFrom < 1 || bracket.playersTo < 1) {
      throwErr('playersFrom and playersTo must be positive');
    }
    if (bracket.rankFrom > bracket.rankTo) {
      throwErr('rankFrom cannot be greater than rankTo');
    }
    if (bracket.playersFrom > bracket.playersTo) {
      throwErr('playersFrom cannot be greater than playersTo');
    }
    if (bracket.percentage < 0 || bracket.percentage > 100) {
      throwErr('percentage must be between 0 and 100');
    }
  }

  const playerRangeGroups = new Map<string, CreatePayoutBracketDto[]>();
  const sortedByPlayers = [...brackets].sort((a, b) => a.playersFrom - b.playersFrom);

  for (let i = 0; i < sortedByPlayers.length; i++) {
    const current = sortedByPlayers[i];
    const key = `${current.playersFrom}-${current.playersTo}`;
    const prevBracket = sortedByPlayers[i - 1];

    if (!playerRangeGroups.has(key)) {
      if (prevBracket && prevBracket.playersTo >= current.playersFrom) {
        throwErr(
          `Players range overlap between [${prevBracket.playersFrom}, ${prevBracket.playersTo}] and [${current.playersFrom}, ${current.playersTo}]`,
        );
      }
      if (prevBracket && current.playersFrom !== prevBracket.playersTo + 1) {
        throwErr(
          `Missing gap in players range. Expected next range to start at ${prevBracket.playersTo + 1} but got ${current.playersFrom}`,
        );
      }
      playerRangeGroups.set(key, []);
    }

    playerRangeGroups.get(key)!.push(current);
  }

  for (const [rangeKey, groupBrackets] of playerRangeGroups) {
    const sortedByRank = [...groupBrackets].sort((a, b) => a.rankFrom - b.rankFrom);

    for (let i = 0; i < sortedByRank.length - 1; i++) {
      const current = sortedByRank[i];
      const next = sortedByRank[i + 1];
      if (current.rankTo >= next.rankFrom) {
        throwErr(
          `Rank range overlap in player range [${current.playersFrom}, ${current.playersTo}] between ranks [${current.rankFrom}, ${current.rankTo}] and [${next.rankFrom}, ${next.rankTo}]`,
        );
      }
    }

    let totalDistributed = 0;
    for (const bracket of groupBrackets) {
      totalDistributed += bracket.percentage;
    }

    if (Math.abs(totalDistributed - 100) > 0.01) {
      throwErr(`Sum of bracket percentages in range ${rangeKey} != 100%. Expected 100, got ${totalDistributed}`);
    }
  }
};
